/**
 * Serialise a `Date` the user picked as a calendar date the backend can accept
 * (`YYYY-MM-DD`; its `@IsDateString()` validators take an ISO date or datetime).
 *
 * **Deliberately built from the local getters, not `toISOString()`.** A date
 * picker yields local midnight, and in Australia that is the *previous* day in
 * UTC — so `toISOString().split('T')[0]` silently reports a receipt as arriving
 * a day early for every user in the country. The whole point of this helper is
 * that the calendar date the lawyer clicked is the calendar date that is sent.
 *
 * Use it for a date the user *chose* on a calendar. It is not for a timestamp
 * that carries a real instant (a `sentAt`, a `createdAt`) — those keep their
 * timezone.
 */
export function toIsoDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * The inverse of {@link toIsoDateOnly}: read a backend `YYYY-MM-DD` calendar date
 * back into a local-midnight `Date`, for a picker to display.
 *
 * **Deliberately not `new Date('2026-10-14')`.** That parses as *UTC* midnight,
 * which is the previous calendar day everywhere west of Greenwich — so a date the
 * lawyer picked would render a day early for those users, the same off-by-one
 * `toIsoDateOnly` exists to prevent in the other direction. Building from the
 * numeric parts pins it to local midnight, so a round trip through the pair is
 * lossless.
 *
 * Returns `undefined` for null/empty/malformed input, so a caller can hand the
 * result straight to a picker's optional `date` prop.
 */
export function fromIsoDateOnly(
  value: string | null | undefined
): Date | undefined {
  if (!value) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return undefined;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}
